// src/page/UniversityHeadRegistration.js

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Box,
    Typography,
    Alert,
    Badge,
    Paper,
    Divider,
    useMediaQuery,
    useTheme,
    InputAdornment,
    Chip,
    Stepper,
    Step,
    StepLabel,
    Fade,
    Zoom,
    Container,
    Card,
    CardContent,
    CircularProgress,
    LinearProgress,
    FormHelperText,
    IconButton,
    Grid,
    Autocomplete,
    Avatar
} from '@mui/material';
import { styled, alpha as muiAlpha } from '@mui/material/styles';
import {
    Person,
    Email,
    Phone,
    School,
    Flag,
    Lock,
    Visibility,
    VisibilityOff,
    CheckCircle,
    Cancel,
    Login,
    ArrowBack,
    Send,
    ArrowBackIos,
    AccessTime,
    Email as EmailIcon,
    Security,
    CheckCircleOutlineRounded,
    Dashboard,
    Wc,
    ReportProblem,
    Refresh,
    Inbox,
    Warning as WarningIcon,
    MarkEmailRead,
    AccountBalance as InstitutionHeadIcon
} from '@mui/icons-material';
import useCustomToast from '../hooks/useToast';
import { authService } from '../apiServices/authService';
import { APIUniversityNames } from '../apiServices/APIs';
import { logoIcon } from '../assets';

// Validation constants
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CONTACT_REGEX = /^\d{10}$/;

// Styled Components
const ModernCard = styled(Card)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
    backdropFilter: 'blur(10px)',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(3),
    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
}));

const ActionButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    fontWeight: 'bold',
    py: 1.2,
    borderRadius: 2,
    transition: 'all 0.3s ease',
    fontSize: '0.95rem',
    '&:hover': {
        transform: 'translateY(-2px)',
    }
}));

const LogoImage = styled('img')(({ theme }) => ({
    height: 80,
    width: 80,
    borderRadius: 12,
    objectFit: 'contain',
    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))',
}));

// Email Delivery Guide Component
const EmailDeliveryGuide = ({ email, onResend }) => {
    const theme = useTheme();

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                mb: 3,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${theme.palette.warning.light}20 0%, ${theme.palette.warning.main}10 100%)`,
                border: `1px solid ${theme.palette.warning.main}40`,
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <WarningIcon color="warning" sx={{ mt: 0.5, fontSize: 28 }} />
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'warning.main', mb: 1 }}>
                        <Inbox sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Didn't receive the verification email?
                    </Typography>
                    
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, mb: 1.5 }}>
                        Sometimes our automated emails may be filtered by your email provider. 
                        Here's what to check:
                    </Typography>

                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: 1.5,
                        pl: { xs: 0, sm: 3 }
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <Inbox sx={{ fontSize: 20, color: theme.palette.warning.main, mt: 0.5 }} />
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Check Spam / Junk Folder
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    The email may have been mistakenly marked as spam. Please check your spam or junk folder.
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <EmailIcon sx={{ fontSize: 20, color: theme.palette.warning.main, mt: 0.5 }} />
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Check Promotions / Updates Tab (Gmail)
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    If you use Gmail, the email might be in the <strong>Promotions</strong> or <strong>Updates</strong> tab.
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <AccessTime sx={{ fontSize: 20, color: theme.palette.warning.main, mt: 0.5 }} />
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Wait a few minutes
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Email delivery can sometimes take 2-5 minutes. Please wait a moment before requesting a new one.
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <MarkEmailRead sx={{ fontSize: 20, color: theme.palette.warning.main, mt: 0.5 }} />
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Add to Contacts
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Add <strong>no-reply@sjcscholarflow.com</strong> to your contacts to ensure future emails are delivered.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 2, 
                        mt: 2.5,
                        pt: 2,
                        borderTop: `1px solid ${theme.palette.warning.main}30`
                    }}>
                        <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            startIcon={<Refresh />}
                            onClick={onResend}
                            sx={{ 
                                textTransform: 'none',
                                borderRadius: 20,
                                px: 3
                            }}
                        >
                            Resend Verification Email
                        </Button>
                        <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            startIcon={<EmailIcon />}
                            onClick={() => {
                                window.location.href = `mailto:support@sjcscholarflow.com?subject=Institutional%20Research%20Lead%20Verification%20Email%20Not%20Received&body=Dear%20SJC%20ScholarFlow%20Support%2C%0A%0AI%20have%20not%20received%20my%20institutional%20research%20lead%20verification%20email.%20Please%20assist.%0A%0ARegards%2C%0A[Your%20Name]`;
                            }}
                            sx={{ 
                                textTransform: 'none',
                                borderRadius: 20
                            }}
                        >
                            Contact Support
                        </Button>
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                        <strong>Tip:</strong> Check your email address spelling: <strong>{email}</strong>
                    </Typography>
                </Box>
            </Box>
        </Paper>
    );
};

// Success Page Component
const SuccessPage = ({ userData, onRedirect, onResend }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [countdown, setCountdown] = useState(15);
    const [showEmailGuide, setShowEmailGuide] = useState(false);

    useEffect(() => {
        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    onRedirect();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        const guideTimer = setTimeout(() => {
            setShowEmailGuide(true);
        }, 10000);

        return () => {
            clearInterval(timer);
            clearTimeout(guideTimer);
        };
    }, [onRedirect]);

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `${theme.palette.common.auth}`,
                padding: isMobile ? 2 : 3,
            }}
        >
            <Container maxWidth="md">
                <Zoom in={true} timeout={800}>
                    <ModernCard elevation={0}>
                        <CardContent sx={{ p: isMobile ? 3 : 5 }}>
                            <Box sx={{ textAlign: 'center', mb: 4 }}>
                                <Zoom in={true} timeout={1000}>
                                    <Avatar
                                        sx={{
                                            width: isMobile ? 80 : 100,
                                            height: isMobile ? 80 : 100,
                                            margin: '0 auto',
                                            background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                                            animation: 'pulse 2s infinite',
                                            '@keyframes pulse': {
                                                '0%': {
                                                    transform: 'scale(0.95)',
                                                    boxShadow: '0 0 0 0 rgba(245, 158, 11, 0.7)',
                                                },
                                                '70%': {
                                                    transform: 'scale(1)',
                                                    boxShadow: '0 0 0 10px rgba(245, 158, 11, 0)',
                                                },
                                                '100%': {
                                                    transform: 'scale(0.95)',
                                                    boxShadow: '0 0 0 0 rgba(245, 158, 11, 0)',
                                                },
                                            },
                                        }}
                                    >
                                        <CheckCircleOutlineRounded sx={{ fontSize: isMobile ? 50 : 60 }} />
                                    </Avatar>
                                </Zoom>

                                <Typography
                                    variant="h4"
                                    sx={{
                                        mt: 3,
                                        mb: 2,
                                        fontWeight: 'bold',
                                        background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                                        backgroundClip: 'text',
                                        textFillColor: 'transparent',
                                        fontSize: isMobile ? '1.5rem' : '2rem',
                                    }}
                                >
                                    Institutional Research Lead Registration Successful!
                                </Typography>

                                <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                                    Welcome to the SJC ScholarFlow Leadership Team
                                </Typography>

                                <Chip
                                    icon={<InstitutionHeadIcon />}
                                    label="Institutional Research Lead Account Created Successfully"
                                    color="warning"
                                    sx={{ mt: 1, mb: 2 }}
                                />
                            </Box>

                            <Paper
                                elevation={0}
                                sx={{
                                    p: isMobile ? 2 : 3,
                                    mb: 3,
                                    background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                                    borderRadius: 3,
                                    border: '1px solid #fbbf24',
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    sx={{
                                        mb: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        fontWeight: 'bold',
                                        color: '#92400e',
                                    }}
                                >
                                    <InstitutionHeadIcon />
                                    Institutional Research Lead Account Details
                                </Typography>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Person color="action" fontSize="small" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Title
                                                </Typography>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {userData?.title || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Person color="action" fontSize="small" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Full Name
                                                </Typography>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {userData?.full_name || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <EmailIcon color="action" fontSize="small" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Email Address
                                                </Typography>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {userData?.email || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Phone color="action" fontSize="small" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Contact Number
                                                </Typography>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {userData?.contact_phone || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <School color="action" fontSize="small" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Institution
                                                </Typography>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {userData?.university_name || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Flag color="action" fontSize="small" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Nationality
                                                </Typography>
                                                <Typography variant="body1" fontWeight="medium">
                                                    {userData?.nationality || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Wc color="action" fontSize="small" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Gender
                                                </Typography>
                                                <Typography variant="body1" fontWeight="medium" sx={{ textTransform: 'capitalize' }}>
                                                    {userData?.gender || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                            <Badge color="action" fontSize="small" />
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Role
                                                </Typography>
                                                <Typography variant="body1" fontWeight="medium">
                                                    Institutional Research Lead
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>

                            <Paper
                                elevation={0}
                                sx={{
                                    p: isMobile ? 2 : 3,
                                    mb: 3,
                                    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                                    borderRadius: 3,
                                    border: '1px solid #86efac',
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    sx={{
                                        mb: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        fontWeight: 'bold',
                                        color: '#166534',
                                    }}
                                >
                                    <Dashboard />
                                    Your Leadership Capabilities
                                </Typography>

                                <Box sx={{ ml: 2 }}>
                                    <Typography variant="body2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircle sx={{ fontSize: 16, color: '#10B981' }} />
                                        Provide strategic oversight of institutional research activities
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircle sx={{ fontSize: 16, color: '#10B981' }} />
                                        Approve major research initiatives and resource allocation
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircle sx={{ fontSize: 16, color: '#10B981' }} />
                                        Set institutional research policies and strategic direction
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircle sx={{ fontSize: 16, color: '#10B981' }} />
                                        Review institutional research performance and impact
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircle sx={{ fontSize: 16, color: '#10B981' }} />
                                        Drive research excellence and innovation initiatives
                                    </Typography>
                                </Box>
                            </Paper>

                            <Paper
                                elevation={0}
                                sx={{
                                    p: isMobile ? 2 : 3,
                                    mb: 3,
                                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                                    borderRadius: 3,
                                    border: '1px solid #fbbf24',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    <AccessTime color="warning" />
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#92400e', mb: 1 }}>
                                            <EmailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                            Email Verification Required
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#78350f', lineHeight: 1.6 }}>
                                            We've sent a verification link to <strong>{userData?.email}</strong>. 
                                            Please check your inbox and click the link to activate your institutional research lead account.
                                            <br />
                                            <strong>Note:</strong> The verification link will expire in 24 hours. 
                                            Leadership privileges will be granted upon email verification.
                                        </Typography>
                                        
                                        <Button
                                            size="small"
                                            onClick={() => setShowEmailGuide(!showEmailGuide)}
                                            sx={{ 
                                                mt: 1.5,
                                                color: '#92400e',
                                                textTransform: 'none',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(146, 64, 14, 0.1)'
                                                }
                                            }}
                                            startIcon={showEmailGuide ? <Cancel fontSize="small" /> : <ReportProblem fontSize="small" />}
                                        >
                                            {showEmailGuide ? 'Hide' : 'Didn\'t receive the email?'}
                                        </Button>
                                    </Box>
                                </Box>
                            </Paper>

                            {showEmailGuide && (
                                <EmailDeliveryGuide 
                                    email={userData?.email} 
                                    onResend={onResend}
                                />
                            )}

                            <Paper
                                elevation={0}
                                sx={{
                                    p: isMobile ? 2 : 3,
                                    mb: 3,
                                    background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                                    borderRadius: 3,
                                    border: '1px solid #fca5a5',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    <Security color="error" />
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#991b1b', mb: 1 }}>
                                            Security Notice
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#7f1d1d', lineHeight: 1.6 }}>
                                            As an Institutional Research Lead, please keep your login credentials secure. 
                                            Never share your password with anyone. For any suspicious activity, 
                                            contact the system administrator immediately.
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>

                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Redirecting to login page in {countdown} seconds...
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={(countdown / 15) * 100}
                                    sx={{
                                        height: 6,
                                        borderRadius: 3,
                                        mb: 2,
                                        backgroundColor: '#e5e7eb',
                                        '& .MuiLinearProgress-bar': {
                                            background: 'linear-gradient(90deg, #F59E0B, #D97706)',
                                            borderRadius: 3,
                                        },
                                    }}
                                />
                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <Button
                                        variant="outlined"
                                        onClick={onRedirect}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: 2,
                                            borderColor: '#F59E0B',
                                            color: '#F59E0B',
                                            '&:hover': {
                                                borderColor: '#D97706',
                                                backgroundColor: muiAlpha('#F59E0B', 0.1),
                                            },
                                        }}
                                    >
                                        Go to Login Now
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={onResend}
                                        startIcon={<Refresh />}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: 2,
                                            borderColor: '#f59e0b',
                                            color: '#f59e0b',
                                            '&:hover': {
                                                borderColor: '#d97706',
                                                backgroundColor: muiAlpha('#f59e0b', 0.1),
                                            },
                                        }}
                                    >
                                        Resend Email
                                    </Button>
                                </Box>
                            </Box>
                        </CardContent>
                    </ModernCard>
                </Zoom>
            </Container>
        </Box>
    );
};

const UniversityHeadRegistration = () => {
    const navigate = useNavigate();
    const showToast = useCustomToast();
    const isSubmittingRef = useRef(false);
    const [error, setError] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Form state
    const [formData, setFormData] = useState({
        full_name: '',
        title: '',
        email: '',
        contact_phone: '',
        nationality: '',
        gender: '',
        university_id: '',
        university_name: '',
        password: '',
        password_confirmation: ''
    });

    // UI state
    const [universities, setUniversities] = useState([]);
    const [universitySearchInput, setUniversitySearchInput] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccessPage, setShowSuccessPage] = useState(false);
    const [registeredUserData, setRegisteredUserData] = useState(null);
    const [resending, setResending] = useState(false);

    // Validation state
    const [errors, setErrors] = useState({
        full_name: '',
        title: '',
        email: '',
        contact_phone: '',
        nationality: '',
        gender: '',
        university_id: '',
        password: '',
        password_confirmation: '',
        form: ''
    });

    // Step validation state
    const [stepValidations, setStepValidations] = useState({
        0: false,
        1: false,
        2: false
    });

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchUniversities();
    }, []);

    useEffect(() => {
        validateCurrentStep();
    }, [formData, activeStep]);

    const validateCurrentStep = () => {
        let isValid = true;
        const newStepValidations = { ...stepValidations };
        const newErrors = { ...errors };

        switch (activeStep) {
            case 0:
                newErrors.full_name = '';
                newErrors.title = '';
                newErrors.email = '';

                if (!formData.full_name.trim()) {
                    newErrors.full_name = 'Full name is required';
                    isValid = false;
                }

                if (!formData.title) {
                    newErrors.title = 'Title is required';
                    isValid = false;
                }

                if (!formData.email.trim()) {
                    newErrors.email = 'Email is required';
                    isValid = false;
                } else if (!EMAIL_REGEX.test(formData.email)) {
                    newErrors.email = 'Please enter a valid email address';
                    isValid = false;
                }
                break;
            
            case 1:
                newErrors.university_id = '';
                newErrors.contact_phone = '';
                newErrors.nationality = '';
                newErrors.gender = '';

                if (!formData.university_id) {
                    newErrors.university_id = 'Institution selection is required';
                    isValid = false;
                }

                if (!formData.contact_phone.trim()) {
                    newErrors.contact_phone = 'Contact number is required';
                    isValid = false;
                } else if (!CONTACT_REGEX.test(formData.contact_phone)) {
                    newErrors.contact_phone = 'Contact number must be exactly 10 digits';
                    isValid = false;
                }

                if (!formData.nationality.trim()) {
                    newErrors.nationality = 'Nationality is required';
                    isValid = false;
                }

                if (!formData.gender) {
                    newErrors.gender = 'Gender is required';
                    isValid = false;
                }
                break;
            
            case 2:
                newErrors.password = '';
                newErrors.password_confirmation = '';

                if (!formData.password) {
                    newErrors.password = 'Password is required';
                    isValid = false;
                }

                if (!formData.password_confirmation) {
                    newErrors.password_confirmation = 'Please confirm your password';
                    isValid = false;
                } else if (formData.password !== formData.password_confirmation) {
                    newErrors.password_confirmation = 'Passwords do not match';
                    isValid = false;
                }
                break;
            
            default:
                isValid = false;
        }

        newStepValidations[activeStep] = isValid;
        setStepValidations(newStepValidations);
        setErrors(newErrors);
        return isValid;
    };

    const fetchUniversities = async () => {
        setLoading(true);
        setError(null);

        try {
            const api = authService.getApi();
            const response = await api.get(APIUniversityNames);
            const data = Array.isArray(response.data.data) ? response.data.data : [];
            setUniversities(data);
            showToast("Success", `Loaded ${data.length} institutions`, "success");
        } catch (error) {
            let errorMessage = "Failed to load institutions";
            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = "CONNECTION_ERROR";
            }
            setError(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleGoBack = () => {
        navigate('/register');
        showToast("Navigation", "Returning to registration options", "info");
    };
    
    const handleLogIn = () => {
        navigate('/');
        showToast("Navigation", "Redirecting to login page", "info");
    };

    const handleRedirectToLogin = () => {
        navigate('/login');
    };

    const handleResendVerification = async () => {
        if (!registeredUserData?.email) {
            showToast("Error", "No email address found", "error");
            return;
        }

        setResending(true);
        try {
            const response = await authService.resendVerification({
                email: registeredUserData.email
            });

            if (response.status === "success") {
                showToast("Success", "Verification email resent successfully! Please check your inbox.", "success");
                showToast(
                    "Email Sent", 
                    "Please check your inbox, spam, and promotions folders. The verification link expires in 24 hours.",
                    "info",
                    { duration: 8000 }
                );
            } else {
                showToast("Error", response.message || "Failed to resend verification email", "error");
            }
        } catch (error) {
            console.error('Resend verification error:', error);
            showToast("Error", error.message || "Failed to resend verification email", "error");
        } finally {
            setResending(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'contact_phone') {
            const numericValue = value.replace(/\D/g, '').slice(0, 10);
            setFormData(prev => ({ ...prev, [name]: numericValue }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        if (errors[name] || errors.form) {
            setErrors(prev => ({ ...prev, [name]: '', form: '' }));
        }
    };

    const handleUniversitySelect = (event, selectedUniversity) => {
        if (selectedUniversity) {
            setFormData(prev => ({
                ...prev,
                university_id: selectedUniversity.id,
                university_name: selectedUniversity.name
            }));
            setErrors(prev => ({ ...prev, university_id: '' }));
            showToast("Success", `Selected: ${selectedUniversity.name}`, "success");
        } else {
            setFormData(prev => ({
                ...prev,
                university_id: '',
                university_name: ''
            }));
        }
    };

    const validateForm = () => {
        let valid = true;
        const newErrors = {
            full_name: '',
            title: '',
            email: '',
            contact_phone: '',
            nationality: '',
            gender: '',
            university_id: '',
            password: '',
            password_confirmation: '',
            form: ''
        };

        if (!formData.full_name.trim()) {
            newErrors.full_name = 'Full name is required';
            valid = false;
        }

        if (!formData.title) {
            newErrors.title = 'Title is required';
            valid = false;
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
            valid = false;
        } else if (!EMAIL_REGEX.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
            valid = false;
        }

        if (!formData.university_id) {
            newErrors.university_id = 'Institution selection is required';
            valid = false;
        }

        if (!formData.contact_phone.trim()) {
            newErrors.contact_phone = 'Contact number is required';
            valid = false;
        } else if (!CONTACT_REGEX.test(formData.contact_phone)) {
            newErrors.contact_phone = 'Contact number must be exactly 10 digits';
            valid = false;
        }

        if (!formData.nationality.trim()) {
            newErrors.nationality = 'Nationality is required';
            valid = false;
        }

        if (!formData.gender) {
            newErrors.gender = 'Gender is required';
            valid = false;
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
            valid = false;
        }

        if (!formData.password_confirmation) {
            newErrors.password_confirmation = 'Please confirm your password';
            valid = false;
        } else if (formData.password !== formData.password_confirmation) {
            newErrors.password_confirmation = 'Passwords do not match';
            valid = false;
        }

        if (!valid) {
            newErrors.form = 'Please fix all validation errors before submitting';
        }

        setErrors(newErrors);
        return valid;
    };

    const resetForm = () => {
        setFormData({
            full_name: '',
            title: '',
            email: '',
            contact_phone: '',
            nationality: '',
            gender: '',
            university_id: '',
            password: '',
            password_confirmation: ''
        });
        setActiveStep(0);
        setUniversitySearchInput('');
        setErrors({
            full_name: '',
            title: '',
            email: '',
            contact_phone: '',
            nationality: '',
            gender: '',
            university_id: '',
            password: '',
            password_confirmation: '',
            form: ''
        });
    };

    const handleNextStep = () => {
        if (validateCurrentStep()) {
            setActiveStep(prev => Math.min(prev + 1, 2));
            showToast("Step Complete", `Step ${activeStep + 1} completed successfully`, "success");
        } else {
            showToast("Validation Error", "Please complete all fields in the current step correctly", "warning");
        }
    };

    const handlePreviousStep = () => {
        setActiveStep(prev => Math.max(prev - 1, 0));
    };

    const handleSubmit = async () => {
         // Prevent concurrent double submissions synchronously
        if (isSubmitting || isSubmittingRef.current) {
            return;
        }

        if (!validateForm()) {
            showToast("Validation Error", "Please fix all validation errors before submitting", "error");
            return;
        }

        setIsSubmitting(true);
        showToast("Processing", "Creating your institutional research lead account...", "info");

        try {
            const registrationData = {
                full_name: formData.full_name,
                title: formData.title,
                email: formData.email,
                university_id: formData.university_id,
                password: formData.password,
                password_confirmation: formData.password_confirmation,
                contact_phone: formData.contact_phone,
                nationality: formData.nationality,
                gender: formData.gender
            };

            const response = await authService.registerInstitutionHead(registrationData);

            if (response.status === "success") {
                setRegisteredUserData({
                    full_name: formData.full_name,
                    title: formData.title,
                    email: formData.email,
                    contact_phone: formData.contact_phone,
                    university_name: formData.university_name,
                    nationality: formData.nationality,
                    gender: formData.gender
                });
                
                setShowSuccessPage(true);
                resetForm();

                showToast(
                    "Check Your Email", 
                    "We've sent a verification link to your email. Please check your inbox, spam, and promotions folders.",
                    "info",
                    { duration: 8000 }
                );
            } else {
                showToast("Error", response.message || "Registration failed", "error");
            }
        } catch (error) {
            console.error('Registration error:', error);
            
            if (error.errors) {
                let errorMessages = [];
                for (const field in error.errors) {
                    errorMessages.push(...error.errors[field]);
                }
                showToast("Validation Error", errorMessages.join("\n"), "error");
            } else if (error.message) {
                showToast("Error", error.message, "error");
            } else {
                showToast("Error", "An unexpected error occurred during registration", "error");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const steps = ['Account Information', 'Institution Details', 'Security Setup'];

    if (showSuccessPage) {
        return (
            <SuccessPage 
                userData={registeredUserData} 
                onRedirect={handleRedirectToLogin}
                onResend={handleResendVerification}
            />
        );
    }

    if (loading) return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
            <Typography variant="h6" color="warning" sx={{ ml: 2 }}>
                Loading institutions...
            </Typography>
        </Box>
    );

    return (
        <Box sx={{background: `${theme.palette.common.auth}`}}>
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Zoom in={true}>
                    <ModernCard elevation={0}>
                        <CardContent sx={{ p: isMobile ? 2 : 4 }}>
                            <Box sx={{ textAlign: 'center', mb: 4 }}>
                                <LogoImage src={logoIcon} alt="SJC ScholarFlow" />
                                <Typography 
                                    variant="h4" 
                                    gutterBottom 
                                    sx={{ 
                                        fontSize: isMobile ? '1.5rem' : '2rem',
                                        background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                                        backgroundClip: 'text',
                                        textFillColor: 'transparent',
                                        fontWeight: 'bold',
                                        mt: 2
                                    }}
                                >
                                    Institutional Research Lead Registration
                                </Typography>
                                <Typography variant="subtitle1" color="text.secondary">
                                    Register to provide strategic leadership for your institution's research activities on SJC ScholarFlow
                                </Typography>
                            </Box>

                            <Stepper activeStep={activeStep} sx={{ mb: 4, overflowX: 'auto' }}>
                                {steps.map((label) => (
                                    <Step key={label}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>

                            {error && (
                                <Alert severity="error" sx={{ mb: 3 }} icon={<Cancel />}>
                                    <Typography variant="subtitle1">Error: {error}</Typography>
                                </Alert>
                            )}

                            <Box component="div">
                                {/* Step 1: Account Information */}
                                <Fade in={activeStep === 0} timeout={500}>
                                    <Box sx={{ display: activeStep === 0 ? 'grid' : 'none', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 3 }}>
                                        <FormControl fullWidth error={!!errors.title}>
                                            <InputLabel>Title *</InputLabel>
                                            <Select
                                                name="title"
                                                value={formData.title}
                                                onChange={handleInputChange}
                                                label="Title *"
                                            >
                                                <MenuItem value="">Select Title</MenuItem>
                                                <MenuItem value="Mr">Mr</MenuItem>
                                                <MenuItem value="Mrs">Mrs</MenuItem>
                                                <MenuItem value="Ms">Ms</MenuItem>
                                                <MenuItem value="Dr">Dr</MenuItem>
                                                <MenuItem value="Prof">Prof</MenuItem>
                                            </Select>
                                            {errors.title && (
                                                <FormHelperText>{errors.title}</FormHelperText>
                                            )}
                                        </FormControl>

                                        <TextField
                                            name="full_name"
                                            label="Full Name *"
                                            value={formData.full_name}
                                            onChange={handleInputChange}
                                            fullWidth
                                            error={!!errors.full_name}
                                            helperText={errors.full_name}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Person color="action" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />

                                        <TextField
                                            name="email"
                                            label="Email Address *"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            fullWidth
                                            error={!!errors.email}
                                            helperText={errors.email}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Email color="action" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Box>
                                </Fade>

                                {/* Step 2: Institution Details */}
                                <Fade in={activeStep === 1} timeout={500}>
                                    <Box sx={{ display: activeStep === 1 ? 'block' : 'none' }}>
                                        <Box sx={{ mb: 3 }}>
                                            <Autocomplete
                                                id="university-select"
                                                options={universities}
                                                loading={loading}
                                                value={universities.find(u => u.id === formData.university_id) || null}
                                                onChange={handleUniversitySelect}
                                                inputValue={universitySearchInput}
                                                onInputChange={(event, newInputValue) => {
                                                    setUniversitySearchInput(newInputValue);
                                                }}
                                                getOptionLabel={(option) => `${option.name} ${option.code ? `(${option.code})` : ''}`}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                renderOption={(props, option) => (
                                                    <li {...props}>
                                                        <Box>
                                                            <Typography variant="body1">
                                                                <strong>{option.name}</strong>
                                                            </Typography>
                                                            {option.code && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Code: {option.code}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </li>
                                                )}
                                                renderInput={(params) => {
                                                    const { InputProps, ...rest } = params;
                                                    return (
                                                        <TextField
                                                            {...rest}
                                                            label="Select Institution / University *"
                                                            required
                                                            error={!!errors.university_id}
                                                            helperText={errors.university_id || "Search and select your institution"}
                                                            InputProps={{
                                                                ...InputProps,
                                                                startAdornment: (
                                                                    <>
                                                                        <InputAdornment position="start">
                                                                            <School color="action" />
                                                                        </InputAdornment>
                                                                        {InputProps?.startAdornment}
                                                                    </>
                                                                ),
                                                                endAdornment: (
                                                                    <>
                                                                        {loading && <CircularProgress color="inherit" size={20} />}
                                                                        <Badge 
                                                                            badgeContent={universities.length} 
                                                                            color="warning"
                                                                            sx={{ mr: 1 }}
                                                                        />
                                                                        {InputProps?.endAdornment}
                                                                    </>
                                                                ),
                                                            }}
                                                        />
                                                    );
                                                }}
                                            />
                                        </Box>

                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    name="contact_phone"
                                                    label="Contact Phone *"
                                                    value={formData.contact_phone}
                                                    onChange={handleInputChange}
                                                    fullWidth
                                                    error={!!errors.contact_phone}
                                                    helperText={errors.contact_phone || "Must be exactly 10 digits"}
                                                    placeholder="0712345678"
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Phone color="action" />
                                                            </InputAdornment>
                                                        ),
                                                        inputProps: {
                                                            maxLength: 10,
                                                            pattern: "[0-9]*",
                                                            inputMode: "numeric"
                                                        }
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    name="nationality"
                                                    label="Nationality *"
                                                    value={formData.nationality}
                                                    onChange={handleInputChange}
                                                    fullWidth
                                                    error={!!errors.nationality}
                                                    helperText={errors.nationality}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Flag color="action" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth error={!!errors.gender}>
                                                    <InputLabel>Gender *</InputLabel>
                                                    <Select
                                                        name="gender"
                                                        value={formData.gender}
                                                        onChange={handleInputChange}
                                                        label="Gender *"
                                                    >
                                                        <MenuItem value="">Select Gender</MenuItem>
                                                        <MenuItem value="male">Male</MenuItem>
                                                        <MenuItem value="female">Female</MenuItem>
                                                        <MenuItem value="other">Other</MenuItem>
                                                    </Select>
                                                    {errors.gender && (
                                                        <FormHelperText>{errors.gender}</FormHelperText>
                                                    )}
                                                </FormControl>
                                            </Grid>
                                        </Grid>

                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2.5,
                                                borderRadius: 2,
                                                marginTop: 3,
                                                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 30%)',
                                                border: '1px solid #fbbf24',
                                                position: 'relative',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                <EmailIcon color="warning" sx={{ mt: 0.5 }} />
                                                <Box>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#92400e', mb: 1 }}>
                                                        Important: Email Verification
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: '#78350f', lineHeight: 1.6 }}>
                                                        After registration, you'll receive a verification email at <strong>{formData.email || 'your email address'}</strong>.
                                                        <br />
                                                        <WarningIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                                                        <strong>Tip:</strong> If you don't see the email within 2 minutes, check your <strong>Spam</strong>, <strong>Junk</strong>, or <strong>Promotions</strong> folder.
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Paper>

                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2.5,
                                                borderRadius: 2,
                                                marginTop: 3,
                                                background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                                                border: '1px solid #fbbf24',
                                                position: 'relative',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                <InstitutionHeadIcon color="warning" sx={{ mt: 0.5 }} />
                                                <Box>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#92400e', mb: 1 }}>
                                                        Institutional Research Lead Leadership Privileges
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                                        As an Institutional Research Lead, you will provide strategic oversight of institutional research, 
                                                        approve major initiatives, manage resources, and drive research excellence across your institution.
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Box>
                                </Fade>

                                {/* Step 3: Security Setup */}
                                <Fade in={activeStep === 2} timeout={500}>
                                    <Box sx={{ display: activeStep === 2 ? 'grid' : 'none', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 3 }}>
                                        {/* Password Field */}
                                        <TextField
                                            name="password"
                                            label="Password *"
                                            type={showPassword ? 'text' : 'password'}
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            fullWidth
                                            error={!!errors.password}
                                            helperText={errors.password || "Can be simple: words, letters, or numbers"}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Lock color="action" />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end" sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <IconButton
                                                            type="button"
                                                            onClick={() => setShowPassword((prev) => !prev)}
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            title={showPassword ? "Hide password" : "Show password"}
                                                            aria-label={showPassword ? "Hide password" : "Show password"}
                                                            sx={{
                                                                color: showPassword ? '#F59E0B' : '#757575',
                                                                padding: '8px',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                minWidth: '40px',
                                                                minHeight: '40px',
                                                                cursor: 'pointer',
                                                                transition: 'color 0.2s ease',
                                                                '&:hover': {
                                                                    color: '#D97706'
                                                                }
                                                            }}
                                                        >
                                                            {showPassword ? (
                                                                <VisibilityOff sx={{ fontSize: 24, display: 'block', minWidth: 24, minHeight: 24 }} />
                                                            ) : (
                                                                <Visibility sx={{ fontSize: 24, display: 'block', minWidth: 24, minHeight: 24 }} />
                                                            )}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />

                                        {/* Confirm Password Field */}
                                        <TextField
                                            name="password_confirmation"
                                            label="Confirm Password *"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            value={formData.password_confirmation}
                                            onChange={handleInputChange}
                                            fullWidth
                                            error={!!errors.password_confirmation}
                                            helperText={errors.password_confirmation}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Lock color="action" />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end" sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <IconButton
                                                            type="button"
                                                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            title={showConfirmPassword ? "Hide password" : "Show password"}
                                                            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                                                            sx={{
                                                                color: showConfirmPassword ? '#F59E0B' : '#757575',
                                                                padding: '8px',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                minWidth: '40px',
                                                                minHeight: '40px',
                                                                cursor: 'pointer',
                                                                transition: 'color 0.2s ease',
                                                                '&:hover': {
                                                                    color: '#D97706'
                                                                }
                                                            }}
                                                        >
                                                            {showConfirmPassword ? (
                                                                <VisibilityOff sx={{ fontSize: 24, display: 'block', minWidth: 24, minHeight: 24 }} />
                                                            ) : (
                                                                <Visibility sx={{ fontSize: 24, display: 'block', minWidth: 24, minHeight: 24 }} />
                                                            )}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Box>
                                </Fade>

                                {errors.form && (
                                    <Box sx={{ mt: 2 }}>
                                        <Alert severity="error">{errors.form}</Alert>
                                    </Box>
                                )}

                                {/* Bottom Step Navigation Buttons (Back & Next) */}
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 4, mb: 2 }}>
                                    {activeStep > 0 && (
                                        <Button 
                                            variant="outlined"
                                            onClick={handlePreviousStep}
                                            startIcon={<ArrowBack />}
                                            disabled={isSubmitting}
                                            sx={{ minWidth: 130, py: 1.2, borderRadius: 2 }}
                                        >
                                            Back
                                        </Button>
                                    )}
                                    {activeStep < 2 && (
                                        <Button 
                                            variant="contained"
                                            onClick={handleNextStep}
                                            disabled={!stepValidations[activeStep]}
                                            sx={{
                                                minWidth: 130,
                                                py: 1.2,
                                                borderRadius: 2,
                                                background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                                                '&:hover': {
                                                    background: 'linear-gradient(135deg, #D97706, #B45309)',
                                                }
                                            }}
                                        >
                                            Next
                                        </Button>
                                    )}
                                </Box>

                                {/* Step 0 Additional Navigation Actions */}
                                {activeStep === 0 && (
                                    <Box sx={{ mt: 3 }}>
                                        <Divider sx={{ my: 2 }} />
                                        <Grid container spacing={2} justifyContent="center">
                                            <Grid item xs={12} sm={6}>
                                                <ActionButton
                                                    fullWidth
                                                    variant="outlined"
                                                    color="secondary"
                                                    startIcon={<ArrowBackIos />}
                                                    onClick={handleGoBack}
                                                    sx={{
                                                        borderColor: '#FF6B6B',
                                                        color: '#FF6B6B',
                                                        borderRadius: 20,
                                                        '&:hover': {
                                                            borderColor: '#FF6B6B',
                                                            backgroundColor: muiAlpha('#FF6B6B', 0.1),
                                                        }
                                                    }}
                                                >
                                                    Back to Registration Options
                                                </ActionButton>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <ActionButton
                                                    fullWidth
                                                    variant="contained"
                                                    startIcon={<Login />}
                                                    onClick={handleLogIn}
                                                    sx={{
                                                        borderRadius: 20,
                                                        background: 'linear-gradient(135deg, #10B981, #059669)',
                                                        '&:hover': {
                                                            background: 'linear-gradient(135deg, #059669, #047857)',
                                                        }
                                                    }}
                                                >
                                                    Sign In to Existing Account
                                                </ActionButton>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )}

                                {/* Step 3 Final Registration Action */}
                                {activeStep === 2 && (
                                    <Box sx={{ mt: 3 }}>
                                        <Button
                                            variant="contained"
                                            color="warning"
                                            size="large"
                                            disabled={isSubmitting || !stepValidations[2]}
                                            fullWidth
                                            onClick={handleSubmit}
                                            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Send />}
                                            sx={{ 
                                                py: 1.5, 
                                                fontSize: '1.1rem',
                                                background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                                                '&:hover': {
                                                    background: 'linear-gradient(135deg, #D97706, #B45309)',
                                                }
                                            }}
                                        >
                                            {isSubmitting ? 'Creating Your Account...' : 'Register as Institutional Research Lead'}
                                        </Button>

                                        {isSubmitting && (
                                            <Box sx={{ width: '100%', mt: 1 }}>
                                                <LinearProgress
                                                    sx={{ 
                                                        height: 4,
                                                        borderRadius: 2,
                                                        backgroundColor: '#fef3c7',
                                                        '& .MuiLinearProgress-bar': {
                                                            background: 'linear-gradient(90deg, #F59E0B, #D97706)',
                                                        }
                                                    }}
                                                />
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                                                    Setting up your institutional research lead account...
                                                </Typography>
                                            </Box>
                                        )}

                                        <Button
                                            variant="outlined"
                                            color="warning"
                                            size="large"
                                            fullWidth
                                            sx={{ mt: 2 }}
                                            onClick={() => navigate('/login')}
                                            startIcon={<Login />}
                                            disabled={isSubmitting}
                                        >
                                            Already have an account? Sign In
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </ModernCard>
                </Zoom>
            </Container>
        </Box>
    );
};

export default UniversityHeadRegistration;

